import { useTranslation } from 'react-i18next';

import {
  type UseRichTextEditorOptions,
  RichTextEditor as BaseRichTextEditor,
  useRichTextEditor as useBaseRichTextEditor,
} from '@valwin/facto/components';

import {
  BoldButton,
  BulletListButton,
  H1Button,
  H2Button,
  ItalicButton,
  OrderedListButton,
  QuoteButton,
  RedoButton,
  UnderlineButton,
  UndoButton,
} from './buttons';
import { Image, ImageButton, ImagePopover, ImageUploadCrop } from './image';
import { LinkButton } from './link-button';
import { Toolbar } from './toolbar';

/**
 * useRichTextEditor with the translated placeholder for group-backoffice
 *
 * @example
 * ```tsx
 * const editor = useRichTextEditor({
 *   value,
 *   onChange,
 *   features: [],
 *   onImageUpload: handleUpload,
 *   imageSizes: ['small', 'medium', 'large'],
 * });
 *
 * // Default toolbar (the Image button is shown when onImageUpload is set)
 * <RichTextEditor.Root editor={editor}>
 *   <RichTextEditor.Header>
 *     <RichTextEditor.Toolbar />
 *   </RichTextEditor.Header>
 * </RichTextEditor.Root>
 *
 * // Custom toolbar composition
 * <RichTextEditor.Root editor={editor}>
 *   <RichTextEditor.Header>
 *     <RichTextEditor.ToolbarContainer>
 *       <RichTextEditor.BoldButton />
 *       <RichTextEditor.ItalicButton />
 *       <RichTextEditor.Separator />
 *       <RichTextEditor.Image />
 *     </RichTextEditor.ToolbarContainer>
 *   </RichTextEditor.Header>
 * </RichTextEditor.Root>
 * ```
 */
export function useRichTextEditor(options: UseRichTextEditorOptions) {
  const { t } = useTranslation();

  return useBaseRichTextEditor({
    placeholder: t('richTextEditor content placeholder'),
    ...options,
  });
}

export const RichTextEditor = {
  Root: BaseRichTextEditor.Root,
  Header: BaseRichTextEditor.Header,

  // Toolbar with default translated buttons
  Toolbar,

  // Empty Toolbar container for custom composition
  ToolbarContainer: BaseRichTextEditor.Toolbar,

  // Image with translations
  Image,

  // Use it if you need to have custom translations
  ImageRoot: BaseRichTextEditor.Image.Root,
  ImageButton: ImageButton,
  ImagePopover: ImagePopover,
  ImageUploadCrop: ImageUploadCrop,

  // Buttons with translations
  BoldButton,
  ItalicButton,
  UnderlineButton,
  H1Button,
  H2Button,
  BulletListButton,
  OrderedListButton,
  QuoteButton,
  UndoButton,
  RedoButton,

  // Link with translations
  LinkButton,

  Separator: BaseRichTextEditor.Separator,
};
